import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import ProductSelect from "./ProductSelect";
import CouponSelect from "./CouponSelect";
import ActionMenu from "./billing-management/ActionMenu";
import BillingInfo from "./BillingInfo";
import PlatformFeeSelect from "./PlatformFeeSelect";
import { EndDate } from "./FamilyForm";
import { Family, familyProtoToFamily, FormErrors } from "./types";
import { useGetFamilyDetails, useUpdateFamily } from "services/advisor";
import {
  Family as FamilyProto,
  FamilyStatus,
  UpdateFamilyRequest,
} from "protogen/advisors_service_pb";
import { validateFamily } from "./utils";
import { emptyUpdates, updates } from "./updateUtils";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import TimeUtilization from "components/time-utilization/TimeUtilization";
import MetadataSection from "components/common/MetadataSection";
import {
  Advisor,
  Member,
  PlanUtilization,
  UserPermission,
} from "protogen/common_pb";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import TextField from "components/common/TextField";
import CopyTextField from "components/common/CopyTextField";
import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Shuffle,
  UserPlus,
} from "lucide-react";
import LinkRouter from "../navigation/LinkRouter";
import { FEATURE_FLAGS, FeatureFlag } from "components/helpers/GatedComponent";
import {
  useAdminUpdateFeatureFlag,
  useAdminDeactivateIdentity,
} from "services/admin";
import FreeIdentityDialog from "components/admin/FreeIdentityDialog";
import AdvisorSelect from "../creation/AdvisorSelect";
import ReactivationDialog from "./billing-management/ReactivationDialog";

const unobfuscateId = (obfuscatedId: string): number => {
  // Decode the Base64 string to a binary string
  const binaryString = atob(obfuscatedId.replace(/-/g, "+").replace(/_/g, "/"));
  // Convert the binary string to bytes
  const byteArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }
  // Convert the bytes to an integer
  let result = 0;
  for (let i = 0; i < byteArray.length; i++) {
    result = (result << 8) | byteArray[i];
  }
  return result;
};

const FamilyMetadata = ({ familyData }: { familyData: Family }) => {
  const { request } = useUpdateFamily();
  if (familyData.metadata === undefined) return null;
  return (
    <Box sx={{ width: "100%" }}>
      <MetadataSection
        metadata={familyData.metadata}
        saveMetadata={async (metadata) => {
          const data = emptyUpdates(familyData.ref || "");
          data.shouldUpdateMetadata = true;
          data.updatedMetadata = metadata;
          await request(data);
        }}
        entityType={"family"}
      />
    </Box>
  );
};

const MemberFeatureFlag = ({
  flag,
  member,
  onFlagUpdate,
}: {
  flag: FeatureFlag;
  member: Member;
  onFlagUpdate: (flagName: string, enabled: boolean) => void;
}) => {
  const isSmsBotFlag = flag.value === "faye.sms-bot";
  const isEnabled = isSmsBotFlag
    ? member.smsBotEnabled
    : member.enabledFeatureFlags.includes(flag.value);
  const { request, loading } = useAdminUpdateFeatureFlag();

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    await request({
      userRef: member.ref,
      flagName: flag.value,
      enabled: newValue,
    });
    onFlagUpdate(flag.value, newValue);
  };

  return (
    <ListItem sx={{ padding: "4px 0" }}>
      <ListItemText
        primary={flag.title}
        secondary={flag.description}
        primaryTypographyProps={{ variant: "bodySmall" }}
        secondaryTypographyProps={{ variant: "bodySmall" }}
      />
      <Switch
        edge="end"
        size="small"
        onChange={onChange}
        checked={isEnabled}
        disabled={loading}
      />
    </ListItem>
  );
};

const MemberDetailsSection = ({
  member,
  onFlagUpdate,
}: {
  member: Member;
  onFlagUpdate: (memberRef: string, flagName: string, enabled: boolean) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { request: freeIdentity, loading: freeing } =
    useAdminDeactivateIdentity();
  const flags = Object.values(FEATURE_FLAGS);
  const isFreed =
    member.primaryEmail?.startsWith("__") ||
    member.primaryPhone?.startsWith("__");

  const handleFreeIdentity = async () => {
    await freeIdentity({ userRef: member.ref });
    setConfirmOpen(false);
    window.location.reload();
  };

  return (
    <Box sx={{ marginBottom: "12px" }}>
      <Box
        display="flex"
        alignItems="center"
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <UserAvatar user={member} size={32} />
        <Typography variant="body" sx={{ marginLeft: "8px", flexGrow: 1 }}>
          {member.displayName}
        </Typography>
        <Button
          variant="text"
          color="error"
          size="small"
          disabled={isFreed}
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
          sx={{
            marginRight: "12px",
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          {isFreed ? "Identity freed" : "Free identity"}
        </Button>
        <IconButton size="small">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ paddingLeft: "40px", paddingTop: "8px" }}>
          <Typography variant="bodySmallHeavy" sx={{ marginBottom: "4px" }}>
            Feature Flags
          </Typography>
          <List dense sx={{ padding: 0 }}>
            {flags.map((flag) => (
              <MemberFeatureFlag
                key={flag.value}
                flag={flag}
                member={member}
                onFlagUpdate={(flagName, enabled) =>
                  onFlagUpdate(member.ref, flagName, enabled)
                }
              />
            ))}
          </List>
        </Box>
      </Collapse>
      <FreeIdentityDialog
        open={confirmOpen}
        name={member.displayName}
        loading={freeing}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleFreeIdentity}
      />
    </Box>
  );
};

const MemberDetails = ({
  members,
  onMembersUpdate,
}: {
  members: Member[];
  onMembersUpdate: (updatedMembers: Member[]) => void;
}) => {
  const handleFlagUpdate = (
    memberRef: string,
    flagName: string,
    enabled: boolean,
  ) => {
    const updatedMembers = members.map((m) => {
      if (m.ref === memberRef) {
        const newFlags = enabled
          ? [...m.enabledFeatureFlags, flagName]
          : m.enabledFeatureFlags.filter((f) => f !== flagName);
        const updates: Partial<Member> = { enabledFeatureFlags: newFlags };
        if (flagName === "faye.sms-bot") {
          updates.smsBotEnabled = enabled;
        }
        return new Member({ ...m, ...updates });
      }
      return m;
    });
    onMembersUpdate(updatedMembers);
  };

  if (members.length === 0) return null;

  return (
    <Box sx={{ marginTop: "12px" }}>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "12px" }}>
        Member Details
      </Typography>
      {members.map((member) => (
        <MemberDetailsSection
          key={member.ref}
          member={member}
          onFlagUpdate={handleFlagUpdate}
        />
      ))}
    </Box>
  );
};

export default ({
  initialFamily,
  primaryAdvisor,
  planUtilization,
}: {
  initialFamily: FamilyProto;
  primaryAdvisor: Advisor;
  advisorPermissions: UserPermission[];
  planUtilization: PlanUtilization;
}) => {
  const { pushSnack } = useSnackpack();
  const [familyData, setFamilyData] = useState<Family>(
    familyProtoToFamily(initialFamily, []),
  );
  const [familyErrors, setFamilyErrors] = useState<FormErrors | null>(null);
  const [localFamily, setLocalFamily] = useState<FamilyProto>(initialFamily);
  const [members, setMembers] = useState<Member[]>(initialFamily.familyMembers);
  const [assignAdvisorModalOpen, setAssignAdvisorModalOpen] = useState(false);
  const [reactivationDialogOpen, setReactivationDialogOpen] = useState(false);
  const [selectedAdvisorRef, setSelectedAdvisorRef] = useState<string | null>(
    primaryAdvisor?.ref || null,
  );
  const { request: requestRefreshFamily } = useGetFamilyDetails();
  const { request: requestUpdate, loading: updateLoading } = useUpdateFamily();

  const refreshFamily = async () => {
    const r = await requestRefreshFamily({
      familyRef: initialFamily.ref,
    });
    if (r?.family) {
      setFamilyData(familyProtoToFamily(r.family, r.advisorPermissions || []));
      setLocalFamily(r.family);
      setMembers(r.family.familyMembers);
    }
  };

  const handleAssignAdvisor = async () => {
    if (selectedAdvisorRef && selectedAdvisorRef !== primaryAdvisor?.ref) {
      const data = emptyUpdates(familyData.ref || "");
      data.updatedAdvisorRef = selectedAdvisorRef;
      const resp = await requestUpdate(data);
      if (resp) {
        await refreshFamily();
        pushSnack({
          message: "Advisor assigned successfully",
          saveNotification: true,
          key: new Date().getTime(),
        });
        setAssignAdvisorModalOpen(false);
        window.location.reload();
      }
    }
  };

  const _validate = () => {
    const famErrors = validateFamily(familyData);
    setFamilyErrors(famErrors);
    let hasErrors = Object.keys(famErrors).length !== 0;
    return !hasErrors;
  };
  const handleSave = async () => {
    if (_validate()) {
      // Not dealing with member or advisor permissions at this point.
      const r = updates(localFamily, familyData, [], []);
      const resp = await requestUpdate(new UpdateFamilyRequest(r));
      if (resp) {
        await refreshFamily();
        pushSnack({
          message: "Family details updated successfully",
          saveNotification: true,
          key: new Date().getTime(),
        });
        if (r.updatedStatus || r.updatedAdvisorRef) {
          // Force a reload as broader state depends on the family status (sidebar, timetracker etc).
          window.location.reload();
        }
      }
    }
  };

  const widthPercentage = 33;
  const [dateOpen, setDateOpen] = useState(false);
  const disabled = updateLoading;
  const canReactivate =
    familyData.status === FamilyStatus.DEACTIVATED ||
    familyData.status === FamilyStatus.DIGITAL_DEACTIVATED ||
    familyData.status === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL;
  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: { xs: "12px", sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
            Advisor details
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={"16px"}
            alignItems="center"
            flexGrow={1}
          >
            {primaryAdvisor && <UserAvatar user={primaryAdvisor} size={40} />}
            <Box display={"flex"} flexDirection={"column"} flexGrow={1}>
              <Typography variant={"bodyHeavy"}>
                {primaryAdvisor?.displayName}
              </Typography>
              <Typography variant={"bodySmall"}>Primary Advisor</Typography>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              gap={"12px"}
              alignItems="center"
            >
              <Typography variant="bodySmall" color="textSecondary">
                <LinkRouter
                  sx={{ alignItems: "center" }}
                  targetNew={true}
                  to={`/inbox/messages?recipientRefs=${encodeURIComponent(
                    primaryAdvisor.ref,
                  )}`}
                >
                  <MessageSquare size={12} style={{ marginRight: 4 }} />
                  Message
                </LinkRouter>
              </Typography>
              <Typography variant="bodySmall" color="textSecondary">
                <LinkRouter
                  sx={{ alignItems: "center" }}
                  targetNew={true}
                  to={`/tools/family-rematch?family=${encodeURIComponent(
                    initialFamily.ref,
                  )}`}
                >
                  <Shuffle size={12} style={{ marginRight: 4 }} />
                  Rematch
                </LinkRouter>
              </Typography>
              <Typography
                variant="bodySmall"
                color="textSecondary"
                sx={{ cursor: "pointer" }}
                onClick={() => setAssignAdvisorModalOpen(true)}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <UserPlus size={12} style={{ marginRight: 4 }} />
                  Assign
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Primary advisor's plan utilization */}
        {familyData.status === FamilyStatus.ACTIVE && (
          <Box>
            <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
              Time utilization
            </Typography>
            <TimeUtilization
              planUtilization={planUtilization}
              serviceEnding={!!familyData?.endDate}
            />
          </Box>
        )}
        {canReactivate && (
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button onClick={() => setReactivationDialogOpen(true)}>
              Reactivate
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ marginBottom: "4px" }}>
        <Typography variant="bodyHeavy">Plan details</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          flexDirection: "row",
        }}
      >
        <DatePicker
          sx={{
            width: `calc(${widthPercentage}% - 8px)`,
            ".MuiButtonBase-root": { marginRight: "unset" },
          }}
          label="Starting date"
          defaultValue={
            familyData.startDate ? new Date(familyData.startDate) : undefined
          }
          disabled={disabled}
          onChange={(date) => {
            if (date === null) return;
            setFamilyData((prev) => ({
              ...prev,
              startDate: date ? date.toLocaleDateString() : null,
            }));
          }}
          format="MM/dd/yyyy"
          open={dateOpen}
          onClose={() => setDateOpen(false)}
          slotProps={{
            textField: {
              error: !!familyErrors?.startDate,
              helperText: familyErrors?.startDate,
              onClick: () => !disabled && setDateOpen(true),
            },
          }}
        />
        <ProductSelect
          disabled={disabled}
          stacked={false}
          value={familyData.product}
          onUpdate={(product) => {
            setFamilyData((prev) => ({
              ...prev,
              product: product,
            }));
          }}
          isAdmin={true}
          widthPercentage={widthPercentage}
          initialProductName={familyData.productName}
        />
        <CouponSelect
          disabled={disabled}
          stacked={false}
          value={familyData.introCoupon}
          onUpdate={(coupon) => {
            setFamilyData((prev) => ({
              ...prev,
              introCoupon: coupon,
            }));
          }}
          isAdmin={true}
          widthPercentage={widthPercentage}
          initialCouponId={familyData.introCouponId}
        />
      </Box>
      <Box display={"flex"} flexDirection={"row"} gap={"12px"}>
        <EndDate
          date={familyData.endDate ? new Date(familyData.endDate) : null}
          onDateChange={(d) =>
            setFamilyData((prev) => ({
              ...prev,
              endDate: d && !isNaN(d.getTime()) ? d.toLocaleDateString() : null,
            }))
          }
          error={familyErrors?.endDate}
          disabled={disabled}
        />
        <PlatformFeeSelect
          value={familyData.platformFeeSchedule}
          onUpdate={(v) =>
            setFamilyData((prev) => ({
              ...prev,
              platformFeeSchedule: v,
            }))
          }
          disabled={disabled}
        />
        {familyData.billingInfo?.stripeCustomerId ? (
          <BillingInfo
            stripeCustomerId={familyData.billingInfo?.stripeCustomerId}
          />
        ) : (
          <TextField
            error={!!familyErrors?.stripeCustomerId}
            helperText={familyErrors?.stripeCustomerId}
            defaultValue={familyData.stripeCustomerId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFamilyData((prev) => ({
                ...prev,
                stripeCustomerId: e.target.value,
              }))
            }
            label="Stripe ID"
            margin="normal"
            size="small"
            sx={{ marginTop: 0, width: "calc(33% - 8px)" }}
            disabled={disabled}
          />
        )}
      </Box>
      <Box>
        <CopyTextField
          label="Family ID"
          value={`${unobfuscateId(familyData.ref || "")}`}
          sx={{
            width: "calc(33% - 8px)",
            borderRadius: "12px",
          }}
        />
      </Box>

      <Box sx={{ marginTop: "12px" }}>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Typography variant="bodyHeavy" sx={{ marginBottom: "12px" }}>
            Family Features
          </Typography>
          <LinkRouter
            to={`/tools/bot-conversations?familyRef=${encodeURIComponent(
              initialFamily.ref,
            )}`}
            sx={{ fontSize: "14px" }}
            useColorScheme
          >
            Bot management
          </LinkRouter>
        </Box>
        <ListItem sx={{ padding: "4px 0" }}>
          <ListItemText
            primary="Enable Lightning"
            secondary="Allow this family to use lightning task delegation features"
            primaryTypographyProps={{ variant: "bodySmall" }}
            secondaryTypographyProps={{ variant: "bodySmall" }}
          />
          <Switch
            edge="end"
            size="small"
            checked={familyData.lightningEnabled === 1}
            onChange={(e) =>
              setFamilyData((prev) => ({
                ...prev,
                lightningEnabled: e.target.checked ? 1 : 0,
              }))
            }
            disabled={disabled}
          />
        </ListItem>
      </Box>

      <MemberDetails members={members} onMembersUpdate={setMembers} />

      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"end"}
        alignItems="end"
        gap={"16px"}
      >
        <FamilyMetadata familyData={familyData} />
        <ActionMenu familyData={familyData} />
        <Button disabled={disabled} onClick={handleSave}>
          Save
        </Button>
      </Box>

      <Dialog
        open={assignAdvisorModalOpen}
        onClose={() => setAssignAdvisorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Primary Advisor</DialogTitle>
        <DialogContent>
          <Box sx={{ paddingTop: "8px" }}>
            <AdvisorSelect
              title="Select Advisor"
              selectedAdvisor={selectedAdvisorRef}
              onChange={(advisorRef) => setSelectedAdvisorRef(advisorRef)}
              isAdmin={true}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignAdvisorModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignAdvisor}
            disabled={
              updateLoading || selectedAdvisorRef === primaryAdvisor?.ref
            }
            variant="contained"
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
      <ReactivationDialog
        familyRef={familyData.ref || ""}
        open={reactivationDialogOpen}
        setOpen={setReactivationDialogOpen}
        onUpdate={() => window.location.reload()}
      />
    </Box>
  );
};
